import { useState, useEffect } from "react";
import { useApi } from "../hooks/useApi";
import Card from "../components/Card";
import Button from "../components/Buttons";
import { Edit } from "lucide-react";
import RiskManageModal from "../components/RiskManagementModal";
import { Plus, FileText, Shield, Building } from 'lucide-react';

// Effective rating: user, then residual, then inherent (blanks skipped), as elsewhere in Axaris.
const effectiveRating = (r) => [r.userRating, r.residualRiskRating, r.inherentRiskRating].find((v) => v && String(v).trim()) || '';

const RiskResults = ({ risks: parentRisks }) => {
  const [risks, setRisks] = useState([]);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const apiCall = useApi();

  // sync with parentRisks whenever it changes
  useEffect(() => {
    setRisks(Array.isArray(parentRisks) ? parentRisks : []);
  }, [parentRisks]);

  const getRiskColor = (rating) => {
    switch (String(rating || '').trim().toUpperCase()) {
      case "CRITICAL":
        return "bg-red-100 text-red-800 border-red-500";
      case "HIGH":
        return "bg-orange-100 text-orange-800 border-orange-500";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-800 border-yellow-500";
      case "LOW":
        return "bg-green-100 text-green-800 border-green-500";
      default:
        return "bg-gray-100 text-gray-800 border-gray-500";
    }
  };

  const updateRiskRating = async (riskId, newRating, reason) => {
    try {
      // The risk lives only in UnifiedRisks (keyed by the GUID id), so route to
      // the unified update-status endpoint.
      await apiCall("PUT", `/unifiedrisks/${riskId}/update-status`, {
        userRating: newRating,
        userReason: reason,
      });
      setRisks((prev) =>
        prev.map((risk) =>
          risk.id === riskId
            ? { ...risk, userRating: newRating, userReason: reason, status: "updated" }
            : risk
        )
      );
    } catch (error) {
      alert("Failed to update risk rating.");
    }
  };

  const acceptRisk = async (riskId, reason) => {
    try {
      await apiCall("PUT", `/unifiedrisks/${riskId}/accept`, {
        userReason: reason,
      });
      setRisks((prev) =>
        prev.map((risk) =>
          risk.id === riskId ? { ...risk, status: "accepted", userReason: reason } : risk
        )
      );
      const risk = risks.find((r) => r.id === riskId);
    } catch (error) {
      alert("Failed to accept risk.");
    }
  };

  if (!risks || risks.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No risks found for this assessment.
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Risk Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(level => (
          <Card key={level} className="text-center">
            <div
              className={`text-2xl font-bold ${level === 'CRITICAL'
                ? 'text-red-600'
                : level === 'HIGH'
                  ? 'text-orange-600'
                  : level === 'MEDIUM'
                    ? 'text-yellow-600'
                    : 'text-green-600'
                }`}
            >
              {
                risks.filter(r => {
                  const rating = String(effectiveRating(r)).trim().toUpperCase();
                  return rating === level;
                }).length
              }
            </div>
            <div className="text-sm text-gray-600">{level} Risks</div>
          </Card>
        ))}
      </div>


      {/* Risk List */}
      <div className="space-y-4">
        {risks.map((risk) => (
          <Card key={risk.id} className={`${risk.status === "accepted" ? "bg-gray-50" : ""}`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <h3 className="text-lg font-semibold mr-4">{risk.name}</h3>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium border ${getRiskColor(
                      effectiveRating(risk)
                    )}`}
                  >
                    {effectiveRating(risk) || '—'}
                  </span>
                  {risk.status === "accepted" && (
                    <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                      Accepted
                    </span>
                  )}
                  {risk.status === "updated" && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      Updated
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Framework: {Array.isArray(risk.frameworkReference) ? risk.frameworkReference.join(", ") : risk.frameworkReference}
                </p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-content-primary">
                  ${risk.estimatedMonetaryImpact?.toLocaleString() || '0'}
                </div>
                <div className="text-sm text-gray-600">Potential Impact</div>
              </div>


            </div>

            <p className="text-gray-700 mb-4">{risk.detailedRiskDescription}</p>

            {risk.impact && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Monetary Impact Analysis:</h4>
                <p className="text-sm text-blue-800">{Math.round(Number(risk.estimatedMonetaryImpact))}</p>
              </div>
            )}

            <div className="mb-4">
              <h4 className="font-medium text-content-primary mb-2">Recommended Controls:</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                {Array.isArray(risk.compensatingControls)
                  ? risk.compensatingControls.map((control, i) => <li key={i}>{control}</li>)
                  : risk.compensatingControls
                    ? risk.compensatingControls.split(",").map((control, i) => <li key={i}>{control.trim()}</li>)
                    : [<li key="default">Standard security controls recommended</li>]}
              </ul>
            </div>

            {risk.userReason && (
              <div className="mb-4 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                <h4 className="font-medium text-yellow-900 mb-1">User Notes:</h4>
                <p className="text-sm text-yellow-800">{risk.userReason}</p>
              </div>
            )}

            <div className="flex justify-end">
              {false && (<Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setSelectedRisk(risk);
                  setShowManageModal(true);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Manage Risk
              </Button>)}
              {/* 
              <Button
                size="sm"
                variant="secondary"
                className="px-1 py-1 h-9 text-xs"
                title="Assign Risk"
              >
                <Shield className="h-3 w-3 mr-1" />
                Assign Risk
              </Button>
              */}
            </div>
          </Card>
        ))}
      </div>

      {false && showManageModal && selectedRisk && (
        <RiskManageModal
          risk={selectedRisk}
          onClose={() => {
            setShowManageModal(false);
            setSelectedRisk(null);
          }}
          onUpdate={(riskId, newRating, reason) => {
            updateRiskRating(riskId, newRating, reason);
            setShowManageModal(false);
            setSelectedRisk(null);
          }}
          onAccept={(riskId, reason) => {
            acceptRisk(riskId, reason);
            setShowManageModal(false);
            setSelectedRisk(null);
          }}
        />
      )}
    </div>
  );
};

export default RiskResults;
